/**
 * FormContainer Component
 *
 * A form container component that wraps child inputs and provides:
 * - Form state management via FormContext
 * - Workflow binding for form submission
 * - Validation based on workflow/schema definitions
 * - Edit mode support for existing records
 * - Loading states and visual feedback
 *
 * Uses flex layout similar to FlexContainer for child arrangement.
 */

import React, { useRef, useMemo, useCallback } from "react";
import { Component, Viewport } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import {
  FormContainerProperties,
  DEFAULT_FORM_CONTAINER_PROPERTIES,
  FormSubmissionResult,
} from "@/types/form.types";
import { cn } from "@/lib/utils";
import { getBreakpointFromViewport } from "@/utils/breakpoint-utils";
import type { ResponsiveBreakpoint } from "@/stores/editor/responsive-store";
import { useInteractionContext } from "@/components/shared/rendering/InteractionContextRuntime";
import { ComponentWrapper } from "@/components/shared/rendering/ComponentWrapper";
import { useResponsiveViewport } from "@/hooks/useResponsiveViewport";
import { FormContextProvider, useFormContext } from "@/contexts/FormContext";
import { FileText, Loader2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface FormContainerProps {
  component: Component;
  isPreview?: boolean;
  children?: Component[];
  position?: { x: number; y: number; z: number };
  size?: { width: number; height: number };
  viewport?: Viewport | ResponsiveBreakpoint;
  activeColorScheme?: ColorScheme;
}

// ============================================================================
// Inner Component (has access to FormContext)
// ============================================================================

interface FormContainerInnerProps {
  component: Component;
  isPreview: boolean;
  children: Component[];
  viewport: Viewport | ResponsiveBreakpoint;
  responsiveProperties: FormContainerProperties;
  activeColorScheme?: ColorScheme;
}

const FormContainerInner: React.FC<FormContainerInnerProps> = ({
  component,
  isPreview,
  children,
  viewport,
  responsiveProperties,
  activeColorScheme,
}) => {
  const interactionContext = useInteractionContext();
  const formContext = useFormContext();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Get loading and error states from form context
  const isLoading = formContext?.isLoading ?? false;
  const isSubmitting = formContext?.state.isSubmitting ?? false;
  const hasErrors = formContext?.state.errors && Object.keys(formContext.state.errors).length > 0;
  const isEmpty = !children || children.length === 0;

  // Container styles
  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: responsiveProperties.flexDirection,
    gap:
      typeof responsiveProperties.gap === "number"
        ? `${responsiveProperties.gap}px`
        : responsiveProperties.gap,
    padding:
      typeof responsiveProperties.padding === "number"
        ? `${responsiveProperties.padding}px`
        : responsiveProperties.padding,
    backgroundColor: responsiveProperties.backgroundColor,
    borderRadius:
      typeof responsiveProperties.borderRadius === "number"
        ? `${responsiveProperties.borderRadius}px`
        : responsiveProperties.borderRadius,
    border:
      component.parentId && component.parentId !== "root"
        ? "none"
        : responsiveProperties.border ||
          (isPreview || interactionContext.mode !== "editing"
            ? "none"
            : "2px dashed #10b981"),
    position: "relative",
    width: "100%",
    minHeight: "100px",
    boxSizing: "border-box",
    opacity: isSubmitting ? 0.7 : 1,
    pointerEvents: isSubmitting ? "none" : "auto",
    transition: "opacity 0.2s ease",
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative w-full transition-all duration-200",
        isEmpty && "hover:border-green-400 hover:bg-green-50/30",
        hasErrors && "border-red-200"
      )}
      style={containerStyle}
      data-component-type="form-container"
      data-form-id={component.id}
      data-workflow-id={responsiveProperties.workflowId}
      data-mode={responsiveProperties.mode}
    >
      {/* Loading overlay */}
      {(isLoading || isSubmitting) && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-lg">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
            <span className="text-sm text-gray-600">
              {isSubmitting ? "Submitting..." : "Loading..."}
            </span>
          </div>
        </div>
      )}

      {isEmpty ? (
        // Empty state
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <FileText className="w-8 h-8 mb-2 opacity-50" />
          <p className="text-sm">
            {responsiveProperties.workflowId || (responsiveProperties.schemaId && responsiveProperties.groupId)
              ? "Add inputs to this form"
              : "Configure workflow to enable form"}
          </p>
          {!isPreview && interactionContext.mode === "editing" && (
            <p className="text-xs mt-1 opacity-70">Use AI chat to add fields</p>
          )}
        </div>
      ) : (
        // Render children
        <div
          className="pointer-events-auto w-full relative"
          style={{
            display: "flex",
            flexDirection: responsiveProperties.flexDirection,
            gap:
              typeof responsiveProperties.gap === "number"
                ? `${responsiveProperties.gap}px`
                : responsiveProperties.gap,
          }}
        >
          {children.map((child) => {
            const childStyles: React.CSSProperties = {
              flexGrow: 0,
              flexShrink: 0,
              flexBasis: "auto",
              minWidth: 0,
              width:
                child.size?.width !== undefined && child.size?.width !== null
                  ? typeof child.size.width === "number"
                    ? `${child.size.width}px`
                    : child.size.width
                  : "100%",
            };

            return (
              <div
                key={child.id}
                className="relative transition-all duration-200 pointer-events-auto"
                style={childStyles}
                data-child-id={child.id}
              >
                <ComponentWrapper
                  component={child}
                  isFlexChild={true}
                  flexDirection={responsiveProperties.flexDirection}
                  viewport={viewport}
                  activeColorScheme={activeColorScheme}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Form label indicator in editor mode */}
      {!isPreview && interactionContext.mode === "editing" && (
        <div className="absolute -top-6 left-0 px-2 py-0.5 text-xs rounded-t-md bg-green-100 text-green-800 border border-b-0 border-green-300">
          <span className="font-medium">
            {responsiveProperties.label || "Form"}
          </span>
          {responsiveProperties.workflowId && (
            <span className="ml-2 opacity-70">
              → {responsiveProperties.workflowName || "Workflow"}
            </span>
          )}
          {responsiveProperties.mode === "edit" && (
            <span className="ml-2 text-blue-600">(Edit Mode)</span>
          )}
        </div>
      )}

      {/* Error summary (shown at bottom when there are validation errors) */}
      {hasErrors && (isPreview || interactionContext.mode === "functional") && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-1">
            <AlertCircle className="w-4 h-4" />
            Please fix the following errors:
          </div>
          <ul className="list-disc list-inside text-sm text-red-600">
            {Object.entries(formContext?.state.errors || {}).map(
              ([field, error]) => (
                <li key={field}>{error}</li>
              )
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main FormContainer Component
// ============================================================================

export const FormContainer: React.FC<FormContainerProps> = ({
  component,
  isPreview = false,
  children = [],
  position: _position = { x: 0, y: 0, z: 0 },
  size: _size = { width: 300, height: 200 },
  viewport = Viewport.DESKTOP,
  activeColorScheme,
}) => {
  const interactionContext = useInteractionContext();
  const { toast } = useToast();

  // Auto-detect responsive breakpoint
  const detectedBreakpoint = useResponsiveViewport();
  const activeViewport = viewport || (detectedBreakpoint as Viewport);

  // Apply responsive overrides
  const responsiveProperties = useMemo(() => {
    const { responsivePosition } = component;
    const breakpoint = getBreakpointFromViewport(activeViewport);

    let props: FormContainerProperties = {
      ...DEFAULT_FORM_CONTAINER_PROPERTIES,
      ...component.properties,
    };

    if (responsivePosition?.breakpoints?.[breakpoint]) {
      const breakpointOverride = responsivePosition.breakpoints[breakpoint];

      if (breakpointOverride.style) {
        props = {
          ...props,
          ...(breakpointOverride.style as Partial<FormContainerProperties>),
        };
      }

      if (breakpointOverride.properties) {
        const bpProps =
          breakpointOverride.properties as Partial<FormContainerProperties>;
        if (bpProps.flexDirection) props.flexDirection = bpProps.flexDirection;
        if (bpProps.gap !== undefined) props.gap = bpProps.gap;
        if (bpProps.padding !== undefined) props.padding = bpProps.padding;
      }
    }

    return props;
  }, [component, activeViewport]);

  // Submit callbacks with toast notifications
  const handleSubmitSuccess = useCallback(
    (result: FormSubmissionResult) => {
      toast({
        title: "Success",
        description: result.message || "Form submitted successfully",
      });
    },
    [toast]
  );

  const handleSubmitError = useCallback(
    (error: Error) => {
      toast({
        title: "Submission Failed",
        description: error.message || "An error occurred while submitting the form",
        variant: "destructive",
      });
    },
    [toast]
  );

  // Wrap in FormContextProvider when in functional/preview mode
  // In editing mode, we don't need the context active
  if (interactionContext.mode === "functional" || isPreview) {
    return (
      <FormContextProvider
        formId={component.id}
        properties={responsiveProperties}
        onSubmitSuccess={handleSubmitSuccess}
        onSubmitError={handleSubmitError}
      >
        <FormContainerInner
          component={component}
          isPreview={isPreview}
          children={children}
          viewport={activeViewport}
          responsiveProperties={responsiveProperties}
          activeColorScheme={activeColorScheme}
        />
      </FormContextProvider>
    );
  }

  // Editor mode: render without FormContext (no form state management needed)
  return (
    <FormContainerInner
      component={component}
      isPreview={isPreview}
      children={children}
      viewport={activeViewport}
      responsiveProperties={responsiveProperties}
      activeColorScheme={activeColorScheme}
    />
  );
};

export default FormContainer;
